<script>
  import { createEventDispatcher, onDestroy, onMount, tick } from 'svelte'

  export let ttlSeconds = 300
  export let busy = false
  export let error = ''
  export let resendIn = 0

  const dispatch = createEventDispatcher()
  const LEN = 6

  let digits = Array(LEN).fill('')
  let inputs = []
  let phase = 'idle' // idle | shake | success
  let entered = false
  let expiresIn = ttlSeconds
  let expireTimer
  let reduceMotion = false

  $: code = digits.join('')
  $: filled = code.length === LEN && digits.every((d) => d !== '')

  onMount(() => {
    reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    requestAnimationFrame(() => {
      entered = true
      focusAt(0)
    })
    expiresIn = ttlSeconds
    expireTimer = setInterval(() => {
      expiresIn = Math.max(0, expiresIn - 1)
    }, 1000)
  })

  onDestroy(() => {
    if (expireTimer) clearInterval(expireTimer)
  })

  export function reset(opts = {}) {
    digits = Array(LEN).fill('')
    phase = 'idle'
    if (typeof opts.ttl === 'number') {
      expiresIn = opts.ttl
      ttlSeconds = opts.ttl
    }
    tick().then(() => focusAt(0))
  }

  export function pulseSuccess() {
    phase = 'success'
  }

  function focusAt(i) {
    const el = inputs[i]
    if (el) el.focus()
  }

  function onInput(i, e) {
    if (busy || phase === 'success') return
    const raw = (e.target.value || '').replace(/\D/g, '')
    if (!raw) {
      digits[i] = ''
      digits = digits
      return
    }
    const chars = raw.split('')
    let cursor = i
    for (const ch of chars) {
      if (cursor >= LEN) break
      digits[cursor] = ch
      cursor += 1
    }
    digits = digits
    if (cursor < LEN) focusAt(cursor)
    else {
      focusAt(LEN - 1)
      if (digits.every((d) => d)) submit()
    }
  }

  function onKeydown(i, e) {
    if (busy || phase === 'success') return
    if (e.key === 'Backspace') {
      if (digits[i]) {
        digits[i] = ''
        digits = digits
      } else if (i > 0) {
        digits[i - 1] = ''
        digits = digits
        focusAt(i - 1)
      }
      e.preventDefault()
      return
    }
    if (e.key === 'ArrowLeft' && i > 0) {
      focusAt(i - 1)
      e.preventDefault()
    }
    if (e.key === 'ArrowRight' && i < LEN - 1) {
      focusAt(i + 1)
      e.preventDefault()
    }
    if (e.key === 'Enter' && filled) {
      e.preventDefault()
      submit()
    }
  }

  function onPaste(e) {
    if (busy || phase === 'success') return
    const text = (e.clipboardData || window.clipboardData).getData('text') || ''
    const nums = text.replace(/\D/g, '').slice(0, LEN).split('')
    if (!nums.length) return
    e.preventDefault()
    digits = Array(LEN)
      .fill('')
      .map((_, i) => nums[i] || '')
    const next = Math.min(nums.length, LEN - 1)
    focusAt(next)
    if (digits.every((d) => d)) submit()
  }

  async function submit() {
    if (!filled || busy || phase === 'success') return
    dispatch('submit', { code })
  }

  export async function failShake() {
    phase = 'shake'
    await new Promise((r) => setTimeout(r, reduceMotion ? 80 : 480))
    phase = 'idle'
    digits = Array(LEN).fill('')
    await tick()
    focusAt(0)
  }

  function onResend() {
    if (busy || resendIn > 0) return
    dispatch('resend')
  }

  function mmss(sec) {
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return `${m}:${String(s).padStart(2, '0')}`
  }
</script>

<div
  class="otp"
  class:otp--entered={entered}
  class:otp--shake={phase === 'shake'}
  class:otp--success={phase === 'success'}
  class:otp--reduce={reduceMotion}
>
  <div class="otp__orb" aria-hidden="true"></div>
  <div class="otp__ring" aria-hidden="true"></div>
  <div class="otp__scan" aria-hidden="true"></div>

  <header class="otp__hero">
    <p class="otp__brand">Lunes Crypto</p>
    <h1 class="otp__title">telegram otp</h1>
    <p class="otp__sub">Шесть цифр · одноразовый · ~{Math.max(1, Math.round(ttlSeconds / 60))} мин</p>
  </header>

  <div class="otp__slots" role="group" aria-label="Код подтверждения" on:paste={onPaste}>
    {#each digits as d, i}
      <input
        class="otp__digit"
        class:otp__digit--filled={d !== ''}
        class:otp__digit--active={false}
        style={`--i:${i}`}
        bind:this={inputs[i]}
        type="text"
        inputmode="numeric"
        autocomplete={i === 0 ? 'one-time-code' : 'off'}
        maxlength="6"
        aria-label={`Цифра ${i + 1}`}
        disabled={busy || phase === 'success'}
        value={d}
        on:input={(e) => onInput(i, e)}
        on:keydown={(e) => onKeydown(i, e)}
        on:focus={(e) => e.target.select()}
      />
    {/each}
  </div>

  {#if error}
    <p class="otp__error" role="alert">{error}</p>
  {/if}

  <div class="otp__meta">
    <span class="otp__timer" class:otp__timer--low={expiresIn <= 60}>
      {expiresIn > 0 ? `ещё ${mmss(expiresIn)}` : 'код истёк'}
    </span>
    <button
      type="button"
      class="otp__resend"
      disabled={busy || resendIn > 0 || expiresIn <= 0}
      on:click={onResend}
    >
      {resendIn > 0 ? `Повтор через ${resendIn}с` : 'Отправить код снова'}
    </button>
  </div>

  <button
    type="button"
    class="otp__submit"
    disabled={!filled || busy || expiresIn <= 0 || phase === 'success'}
    on:click={submit}
  >
    {#if phase === 'success'}
      Готово
    {:else if busy}
      Проверка…
    {:else}
      Подтвердить
    {/if}
  </button>
</div>

<style>
  .otp {
    --otp-ink: var(--pm-ink);
    --otp-muted: var(--pm-ink-soft);
    --otp-mute: var(--pm-ink-mute);
    --otp-bad: var(--pm-bad);
    --otp-line: var(--pm-line);
    --otp-slot: var(--pm-paper);
    position: relative;
    width: min(26rem, 100%);
    padding: 2rem 1.5rem 1.55rem;
    border-radius: 1.5rem;
    border: 1px solid var(--otp-line);
    background: var(--pm-paper);
    box-shadow: var(--pm-shadow-soft);
    overflow: hidden;
    opacity: 0;
    transform: translateY(16px) scale(0.985);
    transition:
      opacity 0.55s cubic-bezier(0.22, 1, 0.36, 1),
      transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .otp--entered {
    opacity: 1;
    transform: none;
  }

  .otp__orb {
    position: absolute;
    width: 16rem;
    height: 16rem;
    left: 50%;
    top: -6.5rem;
    transform: translateX(-50%);
    background: radial-gradient(circle, rgba(var(--pm-ink-rgb), 0.08), transparent 68%);
    pointer-events: none;
    animation: otp-breathe 6s ease-in-out infinite;
  }
  .otp__ring {
    position: absolute;
    inset: -35% -15% auto;
    height: 60%;
    background: radial-gradient(ellipse at 50% 0%, rgba(var(--pm-ink-rgb), 0.04), transparent 60%);
    pointer-events: none;
  }
  .otp__scan {
    position: absolute;
    left: 10%;
    right: 10%;
    top: 40%;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(var(--pm-ink-rgb), 0.18), transparent);
    opacity: 0.4;
    pointer-events: none;
    animation: otp-scan 5s ease-in-out infinite;
  }

  .otp__hero {
    position: relative;
    text-align: center;
    margin-bottom: 1.35rem;
  }
  .otp__brand {
    margin: 0;
    font-family: 'Inter', ui-sans-serif, system-ui, sans-serif;
    font-size: clamp(1.65rem, 5vw, 2rem);
    font-weight: 700;
    letter-spacing: -0.04em;
    color: var(--otp-ink);
    line-height: 1.05;
  }
  .otp__title {
    margin: 0.55rem 0 0;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 0.72rem;
    font-weight: 500;
    letter-spacing: 0.2em;
    text-transform: lowercase;
    color: var(--otp-mute);
  }
  .otp__sub {
    margin: 0.4rem 0 0;
    font-size: 0.82rem;
    color: var(--otp-muted);
  }

  .otp__slots {
    position: relative;
    display: flex;
    justify-content: center;
    gap: clamp(0.35rem, 2vw, 0.5rem);
    margin: 0 auto;
  }
  .otp__digit {
    width: clamp(2.3rem, 11vw, 2.75rem);
    height: clamp(2.75rem, 12vw, 3.2rem);
    border-radius: 0.85rem;
    border: 1px solid var(--otp-line);
    background: var(--otp-slot);
    color: var(--otp-ink);
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 1.4rem;
    font-weight: 600;
    text-align: center;
    caret-color: var(--otp-ink);
    outline: none;
    box-shadow: 0 1px 2px rgba(var(--pm-ink-rgb), 0.06);
    transition:
      border-color 0.18s ease,
      box-shadow 0.18s ease,
      transform 0.18s ease,
      background 0.18s ease;
    opacity: 0;
    transform: translateY(10px);
  }
  .otp--entered .otp__digit {
    opacity: 1;
    transform: none;
    transition-delay: calc(var(--i) * 50ms);
  }
  .otp__digit:focus {
    border-color: rgba(var(--pm-ink-rgb), 0.4);
    box-shadow: 0 0 0 3px rgba(var(--pm-ink-rgb), 0.08);
    transform: translateY(-1px);
  }
  .otp__digit--filled {
    border-color: rgba(var(--pm-ink-rgb), 0.28);
    background: var(--pm-paper-alt);
    animation: otp-fill-pop 0.26s ease;
  }
  .otp__digit:disabled {
    opacity: 0.7;
  }

  .otp__error {
    margin: 0.85rem 0 0;
    padding: 0.5rem 0.65rem;
    border-radius: 0.75rem;
    background: rgba(192, 57, 43, 0.08);
    border: 1px solid rgba(192, 57, 43, 0.2);
    color: var(--otp-bad);
    font-size: 0.82rem;
    text-align: center;
  }

  .otp__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-top: 0.95rem;
    font-size: 0.76rem;
  }
  .otp__timer {
    color: var(--otp-muted);
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-variant-numeric: tabular-nums;
  }
  .otp__timer--low {
    color: var(--pm-warn);
  }
  .otp__resend {
    appearance: none;
    border: 0;
    background: transparent;
    color: var(--otp-ink);
    font: inherit;
    font-weight: 600;
    cursor: pointer;
    padding: 0.2rem 0;
  }
  .otp__resend:hover:not(:disabled) {
    text-decoration: underline;
  }
  .otp__resend:disabled {
    color: var(--otp-mute);
    cursor: not-allowed;
    opacity: 0.75;
  }

  .otp__submit {
    appearance: none;
    display: flex;
    width: 100%;
    margin-top: 1.1rem;
    min-height: 2.55rem;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 999px;
    font: inherit;
    font-weight: 700;
    letter-spacing: -0.01em;
    cursor: pointer;
    color: var(--pm-accent-fg);
    background: var(--pm-accent-gradient);
    box-shadow: 0 8px 24px -10px rgba(var(--pm-ink-rgb), 0.45);
    transition:
      transform 0.15s ease,
      filter 0.15s ease,
      opacity 0.15s ease;
  }
  .otp__submit:hover:not(:disabled) {
    transform: translateY(-1px);
  }
  .otp__submit:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
  }

  .otp--shake .otp__slots {
    animation: otp-shake 0.45s cubic-bezier(0.36, 0.07, 0.19, 0.97);
  }
  .otp--shake .otp__digit {
    border-color: rgba(192, 57, 43, 0.55);
    box-shadow: 0 0 0 2px rgba(192, 57, 43, 0.12);
  }
  .otp--success {
    animation: otp-success 0.65s ease;
  }
  .otp--success .otp__digit {
    border-color: rgba(26, 127, 78, 0.45);
    background: rgba(26, 127, 78, 0.08);
  }
  .otp--success .otp__orb {
    background: radial-gradient(circle, rgba(26, 127, 78, 0.18), transparent 68%);
  }

  .otp--reduce,
  .otp--reduce .otp__orb,
  .otp--reduce .otp__ring,
  .otp--reduce .otp__scan,
  .otp--reduce .otp__digit--filled,
  .otp--reduce.otp--shake .otp__slots,
  .otp--reduce.otp--success {
    animation: none !important;
  }
  .otp--reduce {
    transition: none;
  }

  @keyframes otp-breathe {
    0%,
    100% {
      opacity: 0.65;
      transform: translateX(-50%) scale(1);
    }
    50% {
      opacity: 1;
      transform: translateX(-50%) scale(1.06);
    }
  }
  @keyframes otp-scan {
    0%,
    100% {
      transform: translateY(0);
      opacity: 0.12;
    }
    50% {
      transform: translateY(36px);
      opacity: 0.4;
    }
  }
  @keyframes otp-fill-pop {
    0% {
      transform: scale(0.94);
    }
    60% {
      transform: scale(1.03);
    }
    100% {
      transform: scale(1);
    }
  }
  @keyframes otp-shake {
    0%,
    100% {
      transform: translateX(0);
    }
    20% {
      transform: translateX(-6px);
    }
    40% {
      transform: translateX(6px);
    }
    60% {
      transform: translateX(-4px);
    }
    80% {
      transform: translateX(3px);
    }
  }
  @keyframes otp-success {
    0% {
      box-shadow: var(--pm-shadow-soft);
    }
    40% {
      box-shadow:
        0 0 0 1px rgba(26, 127, 78, 0.25),
        0 18px 48px -16px rgba(26, 127, 78, 0.22);
    }
    100% {
      box-shadow: var(--pm-shadow-soft);
    }
  }

  @media (max-width: 420px) {
    .otp {
      padding: 1.65rem 1rem 1.3rem;
    }
    .otp__meta {
      flex-direction: column;
      align-items: flex-start;
    }
  }
</style>
